import { canonicalizeUrlPath } from '@luxuryescapes/contract-svc-promo/marketing-router/client';

import type { MarketingRouterRule } from '~/types/marketingRouterRule';

type Match = MarketingRouterRule['match'];

/** The match fields an advertising URL can fill, in form order. */
export type ExtractableMatchField =
  | 'regions'
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_term'
  | 'utm_content'
  | 'url';

export type AdUrlExtraction = {
  /** The single value found for each field the URL carries. Absent fields are omitted. */
  values: Partial<Record<ExtractableMatchField, string>>;
  /**
   * A region the path named that the brand does not sell in, set by the caller
   * that knows the codes. It still comes off the path, so the rule matches
   * that page in every region — which the operator is warned about.
   */
  unknownRegion?: string;
  /** Set when the text could not be read as a URL at all, so nothing was extracted. */
  error?: string;
};

/**
 * The UTM query params the router matches on, mapped to the match field each
 * one fills. The router matches no other query param, so a URL's remaining
 * params are ignored rather than reported as unused.
 */
const UTM_PARAM_FIELDS: Array<[string, ExtractableMatchField]> = [
  ['utm_source', 'utm_source'],
  ['utm_medium', 'utm_medium'],
  ['utm_campaign', 'utm_campaign'],
  ['utm_term', 'utm_term'],
  ['utm_content', 'utm_content'],
];

// A path segment the customer portal reads as a language rather than a page.
// Mirrors isLocaleLikeSegment in www-le-customer's UrlLocation: a full locale
// (`en-AU`), its underscore form (`en_AU`), a script-bearing locale
// (`zh-Hant-TW`) or a bare language (`zh`).
const LOCALE_SEGMENT = /^(?:[a-z]{2}-(?:[A-Z][a-z]{3}-)?[A-Z]{2}|[a-z]{2}_(?:[A-Z][a-z]{3}_)?[A-Z]{2}|[a-z]{2})$/;

// A leading segment the customer portal reads as a region. Two letters, as
// getRegionCodeFromPathname matches them — a rule is authored against the page
// the region serves, so the segment comes off whether or not the brand sells
// in it.
const REGION_SEGMENT = /^[a-zA-Z]{2}$/;

/**
 * Splits a pasted landing path into the region it names and the page path the
 * router matches on.
 *
 * The portal strips the region and language segments before evaluating, so
 * `/au/hotels`, `/au/en-AU/hotels` and `/hotels` all evaluate as `/hotels`
 * (see toMarketingRouterUrlPath in www-le-customer, and canonicalizeUrlPath's
 * contract docs). A rule authored against `/au/hotels` therefore matches
 * nobody, which is why the region is lifted out into the Regions field
 * instead of being left on the path.
 *
 * This mirrors the portal's order deliberately: the language segment is
 * removed first, because it sits behind the region and would otherwise be read
 * as the start of the page path.
 */
export function splitLandingPath(pathname: string): { region?: string; url: string } {
  // Repeated slashes are collapsed first, or an interior empty segment shifts
  // the region and locale out of the positions read below — and survives
  // canonicalizeUrlPath, which only strips a trailing slash. `/au//search`
  // would author `//search` while visitors arrive as `/search`.
  const segments = pathname.replace(/\/{2,}/g, '/').split('/');
  // The locale sits at index 2, behind the region, exactly as the portal reads it.
  const withoutLocale = LOCALE_SEGMENT.test(segments[2] ?? '')
    ? [...segments.slice(0, 2), ...segments.slice(3)]
    : segments;

  if (!REGION_SEGMENT.test(withoutLocale[1] ?? '')) {
    return { url: canonicalizeUrlPath(withoutLocale.join('/')) };
  }

  return {
    region: withoutLocale[1].toUpperCase(),
    url: canonicalizeUrlPath(`/${withoutLocale.slice(2).join('/')}`),
  };
}

// Stands in for the host a path-only paste never carried; nothing here reads
// the origin.
const PLACEHOLDER_ORIGIN = 'https://placeholder.invalid';

function parseUrl(text: string, base?: string): URL | null {
  try {
    return new URL(text, base);
  } catch {
    return null;
  }
}

/**
 * Reads the pasted text as a URL, in any shape an operator copies it in.
 *
 * A path pasted on its own (Google Ads shows a "final URL path") is resolved
 * against a base, because `https://` + `au/search` parses `au` as the hostname
 * and loses the region. A host is told from a path by the dot it must carry:
 * `luxuryescapes.com/au` is a host, `au/search` is not. Text carrying
 * whitespace is neither, and is left to fail as unreadable rather than
 * resolved into a path.
 */
function parseAdUrl(text: string): URL | null {
  const absolute = text.startsWith('/') ? null : parseUrl(text);

  if (absolute != null) {
    return absolute;
  }

  const isHostless = text.startsWith('/') || (!/\s/.test(text) && !text.split(/[/?#]/)[0].includes('.'));

  return isHostless ? parseUrl(text, PLACEHOLDER_ORIGIN) : parseUrl(`https://${text}`);
}

/**
 * Reads the match values an advertising URL carries.
 *
 * Every value is returned for the operator to review and edit; nothing here
 * decides what the rule ends up matching. A UTM param the URL lacks leaves its
 * field untouched; every match field is optional.
 */
export function extractMatchFromAdUrl(input: string): AdUrlExtraction {
  const text = input.trim();

  if (text === '') {
    return { values: {}, error: 'Paste an advertising URL first.' };
  }

  const parsed = parseAdUrl(text);

  if (parsed == null) {
    return {
      values: {},
      error: 'That is not a URL we can read. Paste the full advertising URL, including its UTM parameters.',
    };
  }

  const values: Partial<Record<ExtractableMatchField, string>> = {};

  // Query params are matched case-sensitively by the router but written
  // inconsistently by ad platforms, so each param is looked up by its
  // lowercased name.
  const params = new Map<string, string>();
  for (const [name, value] of parsed.searchParams) {
    const trimmed = value.trim();
    if (trimmed !== '' && !params.has(name.toLowerCase())) {
      params.set(name.toLowerCase(), trimmed);
    }
  }

  for (const [param, field] of UTM_PARAM_FIELDS) {
    const value = params.get(param);
    if (value != null) {
      // The portal lower-cases every UTM value it reads (utmCodes in
      // www-le-customer) and the router compares exactly, so `utm_source=Google`
      // is stored as the evaluator will see it.
      values[field] = value.toLowerCase();
    }
  }

  const { region, url } = splitLandingPath(parsed.pathname);

  if (region != null) {
    values.regions = region;
  }
  // A paste carrying no page path says nothing about where the ad lands, so no
  // URL condition is authored. Filling `/` would quietly narrow the rule to the
  // home page — wrong whenever the domain was pasted for its UTMs alone, or the
  // ad URL redirects somewhere deeper.
  if (url !== '/') {
    values.url = url;
  }

  return { values };
}

/** `Object.entries` over an extraction, keeping the field names typed. */
function matchEntries(values: AdUrlExtraction['values']): Array<[ExtractableMatchField, string]> {
  return Object.entries(values) as Array<[ExtractableMatchField, string]>;
}

/**
 * Merges extracted values into a match, adding each value to the field's
 * existing list rather than replacing it: one rule commonly serves several
 * campaigns, and an operator pasting a second URL is usually widening the rule
 * rather than correcting it. Replacing is the caller's decision, made against
 * an empty match.
 */
export function applyExtractionToMatch(match: Match, values: AdUrlExtraction['values']): Match {
  const merged: Match = { ...match };

  for (const [field, value] of matchEntries(values)) {
    const current = merged[field] ?? [];
    merged[field] = current.includes(value) ? current : [...current, value];
  }

  return merged;
}

/**
 * Overwrites each extracted field with the value from the URL, dropping the
 * values that were there before.
 *
 * Only the fields the URL carried are touched: a URL with no `utm_term` says
 * nothing about the term the operator set by hand, so clearing it would
 * discard work the operator never offered up for replacement.
 */
export function replaceExtractionInMatch(match: Match, values: AdUrlExtraction['values']): Match {
  const replaced: Match = { ...match };

  for (const [field, value] of matchEntries(values)) {
    replaced[field] = [value];
  }

  return replaced;
}

/** Whether a match already holds a value in any field an extraction would fill. */
export function hasExtractableMatchValues(match: Match, values: AdUrlExtraction['values']): boolean {
  return matchEntries(values).some(([field]) => (match[field] ?? []).length > 0);
}
